<template>
	<div>
		<PageHeader
			:data="{
				name: 'Time Trackings',
				breadcrumbs: [{ name: 'Home', to: '/' }],
			}"
		/>

		<div class="row">
			<div class="col-12">
				<div class="card">
					<div class="card-body">
						<div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mb-3">
							<div class="time-track-title">
								<h4 class="mb-0">Time tracking overview</h4>
								<small class="text-muted">Grouped by client</small>
							</div>
							<div class="d-flex flex-column flex-md-row gap-2 w-100 w-md-auto ms-md-auto time-track-actions">
								<input
									v-model.trim="searchTerm"
									@input="handleSearchInput"
									type="text"
									class="form-control time-track-search"
									placeholder="Search by client name"
								/>
								<button class="btn btn-outline-secondary" :disabled="loading" @click="fetchTimeTracks()">
									<font-awesome-icon :icon="['fas', loading ? 'spinner' : 'sync']" :class="{ 'fa-spin': loading }" class="me-1" />
									Refresh
								</button>
								<div class="btn-group">
									<button
										type="button"
										class="btn btn-outline-secondary dropdown-toggle"
										data-bs-toggle="dropdown"
										aria-expanded="false"
										:disabled="loading || !timeTracks.length"
									>
										<font-awesome-icon :icon="['fas', 'file-export']" class="me-1" />
										Export CSV file
									</button>
									<ul class="dropdown-menu dropdown-menu-end">
										<li>
											<button class="dropdown-item" type="button" @click="exportCsv('total')">Total time per client</button>
										</li>
										<li>
											<button class="dropdown-item" type="button" @click="exportCsv('users')">User time per client</button>
										</li>
									</ul>
								</div>
							</div>
						</div>

						<div class="d-flex flex-column gap-2 mb-3">
							<div class="filters-toolbar" ref="filtersToolbar">
								<div v-for="filter in activeFilters" :key="filter.key" class="filter-chip">
									<div class="filter-chip__body">
										<font-awesome-icon :icon="filter.icon || ['fas', 'filter']" class="text-muted" />
										<span class="fw-semibold">{{ filter.label }}</span>
										<span class="filter-chip__operator">is</span>
										<template v-if="filter.type === 'user'">
											<select v-model="filter.value" class="form-select form-select-sm filter-chip__select" @change="handleFilterValueChange(filter)">
												<option value="">All users</option>
												<option v-for="user in users" :key="user.user_id" :value="user.user_id">{{ user.user_email }}</option>
											</select>
										</template>
										<template v-else-if="filter.type === 'status'">
											<select v-model="filter.value" class="form-select form-select-sm filter-chip__select" @change="handleFilterValueChange(filter)">
												<option v-for="option in statusOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
											</select>
										</template>
									</div>
									<button class="filter-chip__remove" type="button" @click="removeFilter(filter.key)">
										<font-awesome-icon :icon="['fas', 'times']" />
									</button>
								</div>
								<div class="filters-toolbar__controls">
									<button
										class="filter-trigger"
										type="button"
										ref="filtersButton"
										:aria-expanded="filtersMenuOpen"
										:disabled="!availableFilterOptions.length"
										@click="toggleFiltersMenu"
									>
										<font-awesome-icon :icon="['fas', 'filter']" />
										<span>Filter</span>
									</button>
									<div v-if="filtersMenuOpen" class="filters-menu" :style="filtersMenuStyles">
										<button
											v-for="option in availableFilterOptions"
											:key="option.key"
											type="button"
											class="filters-menu__item"
											@click="activateFilter(option.key)"
										>
											{{ option.label }}
										</button>
									</div>
								</div>
							</div>
						</div>

						<div v-if="loading" class="d-flex justify-content-center py-5">
							<div class="spinner-border" role="status">
								<span class="visually-hidden">Loading...</span>
							</div>
						</div>

						<div v-else>
							<div v-if="timeTracks.length" class="table-responsive">
								<table class="table table-hover align-middle">
									<thead>
										<tr>
											<th scope="col">Client</th>
											<th scope="col">Users</th>
											<th scope="col">Total time</th>
											<th scope="col" class="text-end">Actions</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="(timeTrack, i) in timeTracks" :key="i">
											<td>
												<div class="fw-semibold d-flex align-items-center gap-2">
													<span>{{ timeTrack.name }}</span>
													<span v-if="timeTrack.archive" class="badge bg-warning text-dark">Archived</span>
												</div>
												<small v-if="timeTrack.client_id" class="text-muted">ID: {{ timeTrack.client_id }}</small>
											</td>
											<td>
												<div class="user-badges">
													<span v-for="user in timeTrack.user_clients" :key="user.user_email" class="badge user-badge">
														{{ user.user_name || user.user_email }}
													</span>
												</div>
											</td>
											<td>
												<div class="total-spent-time">
													<font-awesome-icon class="text-secondary me-1" :icon="['fas', 'stopwatch']" />
													<span>{{ totalSpentTime(timeTrack.user_clients) }}</span>
												</div>
											</td>
											<td class="text-end">
												<button
													class="btn btn-warning btn-sm"
													:disabled="!!timeTrack.archive"
													data-bs-toggle="modal"
													data-bs-target="#archiveModal"
													@click="setArchiveClientTracks(timeTrack)"
												>
													<font-awesome-icon :icon="['fas', 'archive']" />
												</button>
												<button
													class="btn btn-primary btn-sm ms-2"
													data-bs-toggle="modal"
													data-bs-target="#trackingsModal"
													@click="setEditClientTracks(timeTrack)"
												>
													<font-awesome-icon :icon="['fas', 'eye']" />
												</button>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
							<div v-else class="text-center py-5 text-muted">No time tracking records match your filters.</div>

							<div v-if="pagination && pagination.last_page > 1" class="mt-3">
								<LPDataTablePagination :pagination="pagination" v-on:getItems="handlePageChange" />
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Modal Tracking Details -->
		<div class="modal fade" id="trackingsModal" tabindex="-1" data-bs-backdrop="static" aria-labelledby="trackingsModalLabel" aria-hidden="true">
			<div class="modal-dialog modal-lg">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title" id="trackingsModalLabel">
							<span v-if="editClientTracks"> {{ editClientTracks.name }}</span>
						</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<template v-if="editClientTracks">
							<div v-for="(userClient, index) in editClientTracks.user_clients" :key="index" class="time-track-user-card">
								<div class="time-track-user-card__header">
									<div class="time-track-user-card__title">
										<span class="fw-semibold">{{ userClient.user_name || userClient.user_email }}</span>
										<span class="text-muted small">{{ userClient.user_email }}</span>
									</div>
									<div class="time-track-user-card__total">
										<font-awesome-icon :icon="['fas', 'stopwatch']" class="me-1" />
										{{ calculateUserTotalTime(userClient.recordings) }}
									</div>
								</div>
								<div class="time-track-user-card__body">
									<div v-for="recording in userClient.recordings" :key="recording.id" class="time-track-row">
										<div class="time-track-row__meta">
											<div class="time-track-row__summary">
												{{ formatPrettyDate(recording.started_at) }}
												<span class="time-track-row__fromto"> from {{ formatTime(recording.started_at) }} to {{ formatTime(recording.ended_at) }} </span>
											</div>
											<div class="time-track-row__range"></div>
										</div>
										<div class="time-track-row__actions">
											<span class="time-track-row__time">
												<font-awesome-icon :icon="['fas', 'stopwatch']" class="me-1" />
												{{ formatSpentLabel(recording.spent_time) }}
											</span>
											<button class="btn btn-outline-light btn-sm time-track-row__edit" @click="editRecord(recording)">
												<font-awesome-icon :icon="['fas', 'pen']" />
											</button>
										</div>
									</div>
								</div>
							</div>
						</template>
						<div class="edit-record border-1" v-if="updatingRecord">
							<hr />
							<p>
								Editing record id <b>{{ recordForEdit.id }}</b> from <b>{{ recordForEdit.user_email }}</b>
							</p>
							<div class="d-flex justify-content-start align-items-center">
								<div class="form-group me-3">
									<label for="">Started At:</label>
									<date-picker type="datetime" v-model="recordForEdit.started_at"></date-picker>
								</div>
								<div class="form-group">
									<label for="">Ended At:</label>
									<date-picker type="datetime" v-model="recordForEdit.ended_at"></date-picker>
								</div>
								<div class="ms-3">
									<button @click="updateRecord()" class="btn btn-primary">Update</button>
									<button @click="cancelEdit()" class="btn btn-outline-secondary ms-2">Cancel</button>
								</div>
							</div>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" data-bs-dismiss="modal" aria-label="Close" class="btn btn-primary">
							Close
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Modal Tracking Details -->
		<div class="modal fade" id="archiveModal" tabindex="-1" data-bs-backdrop="static" aria-labelledby="archiveModalLabel" aria-hidden="true">
			<div class="modal-dialog modal-lg">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title" id="archiveModalLabel">
							<span v-if="archiveClientTracks"> {{ archiveClientTracks.name }}</span>
						</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<p>
							If you archive records for a client, they will not be displayed in the list but will not be deleted from the database. And they will be available if
							needed in the future.
						</p>
					</div>
					<div class="modal-footer">
						<button type="button" data-bs-dismiss="modal" aria-label="Close" class="btn btn-warning">
							Cancel
						</button>
						<button class="btn btn-primary" @click="archiveRecords()">Archive client records</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import PageHeader from '@/components/layout/PageHeader'
import LPDataTablePagination from '@/components/lp-data-table/LPDataTablePagination'
import { mapState } from 'vuex'
import restApi from '../../api/index.js'
import { Modal, Tooltip } from 'bootstrap'
import { handleErrors } from '../../actions/helpers'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'

export default {
	name: 'TimeTrackings',
	components: { PageHeader, DatePicker, LPDataTablePagination },
	data() {
		return {
			timeTracks: [],
			pagination: null,
			loading: false,
			editClientTracks: null,
			archiveClientTracks: null,
			searchTerm: '',
			updatingRecord: false,
			recordForEdit: {},
			trackingsModal: null,
			archiveModal: null,
			users: [],
			filters: {
				user_id: '',
				status: 'active',
			},
			filterOptions: [
				{
					key: 'user',
					label: 'User',
					type: 'user',
					icon: ['fas', 'user'],
				},
				{
					key: 'status',
					label: 'Status',
					type: 'status',
					icon: ['fas', 'archive'],
				},
			],
			activeFilters: [],
			filtersMenuOpen: false,
			filtersMenuStyles: {},
			searchDebounce: null,
		}
	},
	computed: {
		...mapState(['authUser']),
		availableFilterOptions() {
			const activeKeys = this.activeFilters.map(filter => filter.key)
			return this.filterOptions.filter(option => !activeKeys.includes(option.key))
		},
		statusOptions() {
			return [
				{ label: 'Active', value: 'active' },
				{ label: 'Archived', value: 'archived' },
				{ label: 'All', value: 'all' },
			]
		},
	},
	watch: {
		'$route.query': {
			handler() {
				this.applyQueryParams()
				this.fetchTimeTracks()
			},
			deep: true,
		},
	},
	created() {
		this.activateFilter('status')
		this.applyQueryParams()
		this.fetchTimeTracks()
	},
	mounted() {
		this.trackingsModal = new Modal('#trackingsModal')
		this.archiveModal = new Modal('#archiveModal')
		document.addEventListener('click', this.handleClickOutside)
		window.addEventListener('resize', this.refreshFiltersMenuPosition)
		window.addEventListener('scroll', this.refreshFiltersMenuPosition, true)
	},
	beforeDestroy() {
		document.removeEventListener('click', this.handleClickOutside)
		window.removeEventListener('resize', this.refreshFiltersMenuPosition)
		window.removeEventListener('scroll', this.refreshFiltersMenuPosition, true)
	},
	methods: {
		fetchTimeTracks(page = null) {
			const pageFromQuery = parseInt(this.$route.query.page, 10)
			const currentPage = page || (Number.isNaN(pageFromQuery) ? 1 : pageFromQuery)
			this.loading = true
			restApi
				.get('/time-trackings', {
					params: this.getFilterParams(currentPage),
				})
				.then(({ data }) => {
					this.timeTracks = data.data || []
					this.pagination = data.pagination || null
					if (this.pagination && this.pagination.data) {
						delete this.pagination.data
					}
					this.users = data.filters?.users || []
					this.$nextTick(() => {
						this.initTooltips()
					})
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
				.finally(() => {
					this.loading = false
				})
		},
		handleSearchInput() {
			if (this.searchDebounce) {
				clearTimeout(this.searchDebounce)
			}
			this.searchDebounce = setTimeout(() => {
				this.setQueryParams({ page: 1 })
			}, 350)
		},
		handlePageChange({ page }) {
			this.setQueryParams({ page })
		},
		getFilterParams(page) {
			const params = {
				page,
			}
			if (this.searchTerm) {
				params.client_name = this.searchTerm
			}
			if (this.filters.user_id) {
				params.user_id = this.filters.user_id
			}
			if (this.filters.status) {
				params.status = this.filters.status
			}
			return params
		},
		exportCsv(type = 'total') {
			const params = this.getFilterParams(1)
			delete params.page
			params.export_type = type
			restApi
				.get('/time-trackings/export', {
					params,
					responseType: 'blob',
				})
				.then(response => {
					const blob = new Blob([response.data], { type: 'text/csv' })
					const url = URL.createObjectURL(blob)
					const link = document.createElement('a')
					link.href = url
					link.download = `time-trackings-${new Date().toISOString().split('T')[0]}.csv`
					document.body.appendChild(link)
					link.click()
					document.body.removeChild(link)
					URL.revokeObjectURL(url)
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},
		initTooltips() {
			document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(el => {
				new Tooltip(el)
			})
		},
		activateFilter(key) {
			const option = this.filterOptions.find(filter => filter.key === key)
			const alreadyActive = this.activeFilters.some(filter => filter.key === key)
			if (!option || alreadyActive) {
				return
			}
			const newFilter = { ...option }
			if (option.type === 'user') {
				newFilter.value = this.filters.user_id || ''
			}
			if (option.type === 'status') {
				newFilter.value = this.filters.status || 'active'
			}
			this.activeFilters = [...this.activeFilters, newFilter]
			this.closeFiltersMenu()
		},
		handleFilterValueChange(filter) {
			if (filter.type === 'user') {
				this.filters.user_id = filter.value || ''
			}
			if (filter.type === 'status') {
				this.filters.status = filter.value || 'active'
			}
			this.setQueryParams({ page: 1 })
		},
		removeFilter(key) {
			const filterToRemove = this.activeFilters.find(filter => filter.key === key)
			this.activeFilters = this.activeFilters.filter(filter => filter.key !== key)
			if (!filterToRemove) {
				return
			}
			if (filterToRemove.type === 'user') {
				this.filters.user_id = ''
			}
			if (filterToRemove.type === 'status') {
				this.filters.status = 'all'
			}
			this.setQueryParams({ page: 1 })
		},
		toggleFiltersMenu() {
			if (!this.availableFilterOptions.length) {
				return
			}
			if (this.filtersMenuOpen) {
				this.closeFiltersMenu()
			} else {
				this.updateFiltersMenuPosition()
				this.filtersMenuOpen = true
			}
		},
		closeFiltersMenu() {
			this.filtersMenuOpen = false
		},
		handleClickOutside(event) {
			const toolbar = this.$refs.filtersToolbar
			if (!this.filtersMenuOpen || !toolbar) {
				return
			}
			if (!toolbar.contains(event.target)) {
				this.closeFiltersMenu()
			}
		},
		updateFiltersMenuPosition() {
			const button = this.$refs.filtersButton
			if (!button) {
				this.filtersMenuStyles = {}
				return
			}
			const rect = button.getBoundingClientRect()
			const top = rect.bottom + window.scrollY + 8
			const left = rect.left + window.scrollX
			const width = Math.max(rect.width, 320)
			this.filtersMenuStyles = {
				top: `${top}px`,
				left: `${left}px`,
				width: `${width}px`,
			}
		},
		refreshFiltersMenuPosition() {
			if (this.filtersMenuOpen) {
				this.updateFiltersMenuPosition()
			}
		},

		totalSpentTime(userClients) {
			let totalSeconds = 0

			userClients.forEach(userClient => {
				userClient.recordings.forEach(recording => {
					if (recording.spent_time == null) return
					const [hours, minutes, seconds] = recording.spent_time.split(':').map(Number)
					totalSeconds += hours * 3600 + minutes * 60 + seconds
				})
			})

			const totalHours = Math.floor(totalSeconds / 3600)
			const remainingSeconds = totalSeconds % 3600
			const totalMinutes = Math.floor(remainingSeconds / 60)
			const finalSeconds = remainingSeconds % 60

			return `${String(totalHours).padStart(2, '0')}:${String(totalMinutes).padStart(2, '0')}:${String(finalSeconds).padStart(2, '0')}`
		},

		setEditClientTracks(client) {
			this.editClientTracks = client
		},

		setArchiveClientTracks(client) {
			this.archiveClientTracks = client
		},

		calculateUserTotalTime(recordings) {
			let totalSeconds = 0

			recordings.forEach(recording => {
				if (recording.spent_time == null) return
				const [hours, minutes, seconds] = recording.spent_time.split(':').map(Number)
				totalSeconds += hours * 3600 + minutes * 60 + seconds
			})

			const totalHours = Math.floor(totalSeconds / 3600)
			const remainingSeconds = totalSeconds % 3600
			const totalMinutes = Math.floor(remainingSeconds / 60)
			const finalSeconds = remainingSeconds % 60

			return `${String(totalHours).padStart(2, '0')}:${String(totalMinutes).padStart(2, '0')}:${String(finalSeconds).padStart(2, '0')}`
		},

		formatDate(dateString) {
			const date = new Date(dateString)
			const month = String(date.getMonth() + 1).padStart(2, '0')
			const day = String(date.getDate()).padStart(2, '0')
			const year = date.getFullYear()
			const hours = String(date.getHours()).padStart(2, '0')
			const minutes = String(date.getMinutes()).padStart(2, '0')
			const seconds = String(date.getSeconds()).padStart(2, '0')

			return `${month}-${day}-${year} ${hours}:${minutes}:${seconds}`
		},
		formatPrettyDate(dateString) {
			if (!dateString) {
				return '—'
			}
			const date = new Date(dateString)
			if (Number.isNaN(date.valueOf())) {
				return dateString
			}
			const weekday = date.toLocaleDateString('en-US', { weekday: 'long' })
			const day = date.toLocaleDateString('en-US', { day: 'numeric' })
			const month = date.toLocaleDateString('en-US', { month: 'short' })
			const year = date.toLocaleDateString('en-US', { year: 'numeric' })
			return `${weekday} ${day} ${month} ${year}`
		},
		formatTime(dateString) {
			if (!dateString) {
				return '—'
			}
			const date = new Date(dateString)
			if (Number.isNaN(date.valueOf())) {
				return dateString
			}
			return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
		},
		formatSpentLabel(spentTime) {
			if (!spentTime) {
				return '0h'
			}
			const parts = spentTime.split(':').map(Number)
			if (parts.length !== 3 || parts.some(Number.isNaN)) {
				return spentTime
			}
			const [hours, minutes] = parts
			const displayHours = String(hours).padStart(2, '0')
			const displayMinutes = String(minutes).padStart(2, '0')
			return `${displayHours}h ${displayMinutes}min`
		},
		cancelEdit() {
			this.updatingRecord = false
			this.recordForEdit = {}
		},

		editRecord(record) {
			this.updatingRecord = true
			this.recordForEdit = record
			this.recordForEdit.started_at = new Date(this.recordForEdit.started_at)
			this.recordForEdit.ended_at = new Date(this.recordForEdit.ended_at)
		},

		updateRecord() {
			restApi
				.put(`/time-trackings/${this.recordForEdit.id}`, {
					started_at: this.recordForEdit.started_at,
					ended_at: this.recordForEdit.ended_at,
				})
				.then(() => {
					this.updatingRecord = false
					this.fetchTimeTracks(this.pagination?.current_page || 1)
					this.trackingsModal.hide()
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		archiveRecords() {
			const clientId = this.archiveClientTracks?.client_id || this.archiveClientTracks?.user_clients?.[0]?.recordings?.[0]?.client_id
			if (!clientId) {
				return
			}
			restApi
				.post(`/time-track/archive/${clientId}`)
				.then(() => {
					this.fetchTimeTracks(this.pagination?.current_page || 1)
					this.archiveModal.hide()
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},
		applyQueryParams() {
			const query = this.$route.query
			this.searchTerm = query.client_name ? String(query.client_name) : ''
			this.filters.user_id = query.user_id ? String(query.user_id) : ''
			this.filters.status = query.status ? String(query.status) : 'active'
			if (this.filters.user_id && !this.activeFilters.some(filter => filter.key === 'user')) {
				this.activateFilter('user')
			}
			this.activeFilters = this.activeFilters.map(filter => {
				if (filter.type === 'user') {
					return { ...filter, value: this.filters.user_id || '' }
				}
				if (filter.type === 'status') {
					return { ...filter, value: this.filters.status || 'active' }
				}
				return filter
			})
		},
		buildQueryParams(pageOverride = null) {
			const params = {}
			if (this.searchTerm) {
				params.client_name = this.searchTerm
			}
			if (this.filters.user_id) {
				params.user_id = this.filters.user_id
			}
			if (this.filters.status && this.filters.status !== 'active') {
				params.status = this.filters.status
			}
			const pageValue = pageOverride || this.pagination?.current_page || 1
			if (pageValue && pageValue !== 1) {
				params.page = pageValue
			}
			return params
		},
		setQueryParams({ page } = {}) {
			const params = this.buildQueryParams(page)
			const current = this.$route.query
			const next = { ...params }
			const same = Object.keys(current).length === Object.keys(next).length && Object.keys(next).every(key => String(current[key]) === String(next[key]))
			if (same) {
				this.fetchTimeTracks(page || 1)
				return
			}
			this.$router.replace({ query: next })
		},
	},
}
</script>
<style scoped>
.filters-toolbar {
	display: flex;
	flex-wrap: wrap;
	gap: 0.5rem;
	align-items: center;
}

.filters-toolbar__controls {
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	position: relative;
}

.filter-trigger {
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.45rem 1.1rem;
	border: 1px solid #dcdfe3;
	border-radius: 999px;
	background: #fff;
	font-weight: 600;
	font-size: 0.95rem;
	color: #4b5563;
	min-height: 44px;
	transition: box-shadow 0.2s ease;
}

.filter-trigger:disabled {
	opacity: 0.6;
	pointer-events: none;
}

.filters-menu {
	position: fixed;
	min-width: 320px;
	background: #fff;
	border: 1px solid #e5e7eb;
	border-radius: 0.75rem;
	box-shadow: 0 10px 30px rgba(17, 24, 39, 0.15);
	padding: 0.25rem;
	z-index: 1055;
}

.filters-menu__item {
	display: block;
	width: 100%;
	text-align: left;
	padding: 0.4rem 0.75rem;
	border: none;
	background: transparent;
	font-size: 0.9rem;
	border-radius: 0.5rem;
	color: #111827;
}

.filters-menu__item:hover {
	background-color: #f3f4f6;
}

.filter-chip {
	display: inline-flex;
	align-items: stretch;
	border: 1px solid #dcdfe3;
	border-radius: 999px;
	background-color: #fff;
	overflow: hidden;
	min-height: 44px;
}

.filter-chip__body {
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.35rem 0.75rem;
	white-space: nowrap;
}

.filter-chip__select {
	min-width: 160px;
	border-radius: 999px;
}

.filter-chip__remove {
	border: none;
	background: transparent;
	padding: 0.35rem 0.65rem;
	color: #6b7280;
	display: inline-flex;
	align-items: center;
}

.filter-chip__remove:hover {
	color: #b91c1c;
}

.filter-chip__operator {
	text-transform: uppercase;
	font-size: 0.75rem;
	color: #525866;
	background-color: #f1f3f5;
	padding: 0.15rem 0.5rem;
	border-radius: 999px;
}

.time-track-search {
	max-width: 200px;
}

.time-track-title {
	flex: 0 0 50%;
	max-width: 50%;
}

.time-track-actions {
	justify-content: flex-end;
}

.user-badges {
	display: flex;
	flex-wrap: wrap;
	gap: 0.35rem;
}

.user-badge {
	background-color: #eef2ff;
	color: #1f2937;
	border: 1px solid #dbeafe;
	font-weight: 600;
	padding: 0.35rem 0.6rem;
	border-radius: 999px;
	font-size: 0.78rem;
}

.total-spent-time {
	display: flex;
	align-items: center;
	justify-content: center;
}

.time-track-user-card {
	border: 1px solid #e5e7eb;
	border-radius: 0.75rem;
	padding: 1rem;
	margin-bottom: 1rem;
	background: #f9fafb;
}

.time-track-user-card__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
	padding-bottom: 0.75rem;
	border-bottom: 1px solid #e5e7eb;
}

.time-track-user-card__title {
	display: flex;
	flex-direction: column;
}

.time-track-user-card__total {
	background: #111827;
	color: #fff;
	padding: 0.35rem 0.75rem;
	border-radius: 999px;
	font-size: 0.85rem;
}

.time-track-user-card__body {
	margin-top: 0.75rem;
	display: flex;
	flex-direction: column;
	gap: 0.65rem;
}

.time-track-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 1rem;
	padding: 0.6rem 0.75rem;
	border-radius: 0.65rem;
	background: #fff;
	border: 1px solid #e5e7eb;
}

.time-track-row__meta {
	display: flex;
	flex-direction: column;
	gap: 0.15rem;
}

.time-track-row__date {
	font-weight: 600;
}

.time-track-row__range {
	font-size: 0.75rem;
	color: #6b7280;
}

.time-track-row__summary {
	font-weight: 600;
}

.time-track-row__fromto {
	font-weight: 400;
	color: #4b5563;
	margin-left: 0.35rem;
}

.time-track-row__edit {
	border-radius: 999px;
}

.time-track-row__actions {
	display: flex;
	align-items: center;
	gap: 0.75rem;
}

.time-track-row__time {
	font-weight: 600;
	color: #111827;
}
</style>
